import React from 'react';

/**
 * Lightweight Rich Text Format Toolbar for textareas:
 * Adds [b], [i], [u], [COLOR], [table], [quote], and list formatting buttons!
 */
export const FormatToolbar = ({ textareaId, textValue, setTextValue }) => {
  const insertTag = (startTag, endTag = '') => {
    const el = document.getElementById(textareaId);
    if (!el) {
      setTextValue(prev => prev + `${startTag}${endTag}`);
      return;
    }

    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = textValue.substring(start, end);
    const replacement = `${startTag}${selected || 'text'}${endTag}`;
    const newText = textValue.substring(0, start) + replacement + textValue.substring(end);

    setTextValue(newText);

    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + startTag.length, start + startTag.length + (selected.length || 4));
    }, 50);
  };

  const insertTableTemplate = () => {
    const template = `\n[tbl]\n[tr][th]Header 1[/th][th]Header 2[/th][th]Header 3[/th][/tr]\n[tr][td]Data 1[/td][td]Data 2[/td][td]Data 3[/td][/tr]\n[/tbl]\n`;
    insertTag(template, '');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('Image size exceeds 10MB limit.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
      const API_BASE = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

      const res = await fetch(`${API_BASE}/forum/upload-image`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();

      if (data && data.url) {
        insertTag(`[img]${data.url}[/img]`, '');
      }
    } catch (err) {
      alert('Failed to upload image. Please try again.');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 p-2 bg-[#FAF6EE] border border-gray-300 rounded-t-xl border-b-0 text-xs">
      <button
        type="button"
        onClick={() => insertTag('[b]', '[/b]')}
        className="px-2 py-1 bg-white border border-gray-300 rounded hover:bg-amber-100 font-black text-gray-800"
        title="Bold"
      >
        B
      </button>
      <button
        type="button"
        onClick={() => insertTag('[i]', '[/i]')}
        className="px-2 py-1 bg-white border border-gray-300 rounded hover:bg-amber-100 italic font-serif text-gray-800"
        title="Italic"
      >
        I
      </button>
      <button
        type="button"
        onClick={() => insertTag('[u]', '[/u]')}
        className="px-2 py-1 bg-white border border-gray-300 rounded hover:bg-amber-100 underline text-gray-800"
        title="Underline"
      >
        U
      </button>

      <span className="h-4 w-px bg-gray-300 mx-0.5" />

      {/* Colors */}
      <button
        type="button"
        onClick={() => insertTag('[COLOR=#CC6600]', '[/COLOR]')}
        className="px-2 py-1 bg-orange-100 border border-orange-300 rounded text-[#CC6600] font-bold text-[11px]"
        title="Temple Saffron Color"
      >
        Saffron
      </button>
      <button
        type="button"
        onClick={() => insertTag('[COLOR=#1B5E20]', '[/COLOR]')}
        className="px-2 py-1 bg-emerald-100 border border-emerald-300 rounded text-[#1B5E20] font-bold text-[11px]"
        title="Sacred Green Color"
      >
        Green
      </button>
      <button
        type="button"
        onClick={() => insertTag('[COLOR=#1A237E]', '[/COLOR]')}
        className="px-2 py-1 bg-indigo-100 border border-indigo-300 rounded text-[#1A237E] font-bold text-[11px]"
        title="Royal Navy Color"
      >
        Navy
      </button>

      <span className="h-4 w-px bg-gray-300 mx-0.5" />

      {/* Table & Quote & Image Attachment */}
      <button
        type="button"
        onClick={insertTableTemplate}
        className="px-2.5 py-1 bg-amber-50 border border-amber-400 text-amber-900 rounded font-bold text-[11px] flex items-center gap-1 hover:bg-amber-200"
        title="Insert Data Table Template"
      >
        📊 Table
      </button>
      <button
        type="button"
        onClick={() => insertTag('[quote]', '[/quote]')}
        className="px-2 py-1 bg-white border border-gray-300 rounded hover:bg-amber-100 font-serif text-gray-700 text-[11px]"
        title="Quote"
      >
        “ Quote
      </button>

      <label className="px-2.5 py-1 bg-[#1B5E20] hover:bg-[#2D5A1E] text-white rounded font-bold text-[11px] flex items-center gap-1 cursor-pointer shadow-xs">
        🖼️ Insert Image
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
      </label>
    </div>
  );
};
